import { useCallback, useMemo, useState } from "react";
import {
  KdsPageMealMenuPlateOrderPreviewFragment,
  KdsPageMenuOrderPreviewFragment,
  KdsTicketMealMenuPlateOrderFragment,
  KdsTicketMenuOrderFragment,
  MenuOrderItemStatus,
} from "../../types";
import AnalyzeDialog from "./AnalyzeDialog";
import ChangeStatusDialog from "./ChangeStatusDialog";
import PrintAllDialog from "./PrintAllDialog";

import { reduceStatuses } from "shared-ui/MenuOrder/status";
import BulkSelectDialog from "./BulkSelectDialog";
import KdsTicketStrip from "./KdsTicketStrip";
import ReorderTicketsDialog from "./ReorderTicketsDialog";
import KdsTicket from "./Ticket";
import PlateOrderKDSTicket from "./Ticket/PlateOrderKDSTicket";
import KdsTicketSummary from "./Ticket/Summary";
import { GenericOrderPreview } from "./Ticket/types";
import {
  PickupTimeOverride,
  TicketOrderKey,
  overridesAfterIndexMove,
  overridesFromDesiredOrder,
  sortMenuOrdersWithOverrides,
} from "./ticketReorder";
import { MakeHandleAirPrintFn, PrintMethod, WebPrinterTicketOrder } from "./types";
import { KDSOrderDiningOption, sortMenuOrders } from "./utils";

interface KdsDialogContentProps {
  updateOrderStatus: (order: KdsTicketMenuOrderFragment, status: MenuOrderItemStatus, update?: () => void) => void;
  updatePlateOrderStatus: (plateOrder: KdsTicketMealMenuPlateOrderFragment, status: MenuOrderItemStatus, update?: () => void) => void;
  updateMultipleOrderOrPlateOrderStatuses: (
    orders: KdsTicketMenuOrderFragment[],
    plateOrders: KdsTicketMealMenuPlateOrderFragment[],
    status: MenuOrderItemStatus,
    update?: () => void
  ) => void;
  diningStationIds: string[];
  mealMenuIds: string[];
  menuOrders: readonly KdsPageMenuOrderPreviewFragment[];
  unfilteredMenuOrders: readonly KdsPageMenuOrderPreviewFragment[];
  mealMenuPlateOrders: readonly KdsPageMealMenuPlateOrderPreviewFragment[];
  unfilteredMealMenuPlateOrders: readonly KdsPageMealMenuPlateOrderPreviewFragment[];
  menuOrderItemIdsToDiningStationName: { [menuOrderItemId: string]: string };
  makeHandleAirPrintFn: MakeHandleAirPrintFn;
  printMethod: PrintMethod;
  handleWebPrintOrder?: (ticketOrder: WebPrinterTicketOrder) => Promise<void>;
  selectedDiningOption?: KDSOrderDiningOption;
}

const KdsDialogContent = ({
  makeHandleAirPrintFn,
  handleWebPrintOrder,
  printMethod,
  updateOrderStatus,
  updatePlateOrderStatus,
  updateMultipleOrderOrPlateOrderStatuses,
  mealMenuIds,
  diningStationIds,
  menuOrders,
  unfilteredMenuOrders,
  mealMenuPlateOrders,
  unfilteredMealMenuPlateOrders,
  menuOrderItemIdsToDiningStationName,
  selectedDiningOption,
}: KdsDialogContentProps) => {
  const [selectedWebPrinterTicketOrder, setSelectedWebPrinterTicketOrder] = useState<null | WebPrinterTicketOrder>(null);
  const [printAll, setPrintAll] = useState(false);
  const [displayMenuItemDialog, setDisplayMenuItemDialog] = useState(false);
  const [isReorderDialogOpen, setIsReorderDialogOpen] = useState(false);

  const [isBulkSelect, setIsBulkSelect] = useState<boolean>(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set([]));

  const [selectedPlateOrderIds, setSelectedPlateOrderIds] = useState<Set<string>>(new Set([]));

  const [isBulkSelectDialogOpen, setIsBulkSelectDialogOpen] = useState<boolean>(false);

  // Session-only custom order via effective pickup overrides (NUT-2369). Lost on leave/refresh.
  const [pickupTimeOverrides, setPickupTimeOverrides] = useState<Map<TicketOrderKey, PickupTimeOverride>>(() => new Map());

  const [statusWhitelist, setStatusWhitelist] = useState<MenuOrderItemStatus[]>(["new", "cooking"]);
  const handleCancelOrder = (order: KdsTicketMenuOrderFragment) => updateOrderStatus(order, "cancelled");
  const handleCompleteOrder = (order: KdsTicketMenuOrderFragment) => updateOrderStatus(order, "done");
  const handleCookOrder = (order: KdsTicketMenuOrderFragment) => updateOrderStatus(order, "cooking");
  const handleMarkOrderNew = (order: KdsTicketMenuOrderFragment) => updateOrderStatus(order, "new");

  const handleCancelPlateOrder = (order: KdsTicketMealMenuPlateOrderFragment) => updatePlateOrderStatus(order, "cancelled");
  const handleCompletePlateOrder = (order: KdsTicketMealMenuPlateOrderFragment) => updatePlateOrderStatus(order, "done");
  const handleCookPlateOrder = (order: KdsTicketMealMenuPlateOrderFragment) => updatePlateOrderStatus(order, "cooking");
  const handleMarkPlateOrderNew = (order: KdsTicketMealMenuPlateOrderFragment) => updatePlateOrderStatus(order, "new");

  const handleCompleteOrders = (orders: KdsTicketMenuOrderFragment[], plateOrders: KdsTicketMealMenuPlateOrderFragment[]) =>
    updateMultipleOrderOrPlateOrderStatuses(orders, plateOrders, "done");
  const handleCookOrders = (orders: KdsTicketMenuOrderFragment[], plateOrders: KdsTicketMealMenuPlateOrderFragment[]) =>
    updateMultipleOrderOrPlateOrderStatuses(orders, plateOrders, "cooking");
  const handleMarkOrdersNew = (orders: KdsTicketMenuOrderFragment[], plateOrders: KdsTicketMealMenuPlateOrderFragment[]) =>
    updateMultipleOrderOrPlateOrderStatuses(orders, plateOrders, "new");
  const handleCancelOrders = (orders: KdsTicketMenuOrderFragment[], plateOrders: KdsTicketMealMenuPlateOrderFragment[]) =>
    updateMultipleOrderOrPlateOrderStatuses(orders, plateOrders, "cancelled");

  const handleActivateBulkSelect = () => {
    setIsBulkSelect(true);
    setSelectedOrderIds(new Set([]));
    setSelectedPlateOrderIds(new Set([]));
  };

  const handleCancelBulkSelect = () => {
    setIsBulkSelect(false);
    setSelectedOrderIds(new Set([]));
    setSelectedPlateOrderIds(new Set([]));
  };

  const handleProceedBulkSelect = () => {
    setIsBulkSelectDialogOpen(true);
  };

  const handleDeselectAll = () => {
    setSelectedOrderIds(new Set([]));
    setSelectedPlateOrderIds(new Set([]));
  };

  const handleSelectForBulkSelect = (orderId: string, value: boolean) => {
    if (value) {
      setSelectedOrderIds(new Set([...selectedOrderIds, orderId]));
    } else {
      setSelectedOrderIds(new Set([...selectedOrderIds].filter(id => id !== orderId)));
    }
  };
  const handleSelectPlateForBulkSelect = (plateOrderId: string, value: boolean) => {
    if (value) {
      setSelectedPlateOrderIds(new Set([...selectedPlateOrderIds, plateOrderId]));
    } else {
      setSelectedPlateOrderIds(new Set([...selectedPlateOrderIds].filter(id => id !== plateOrderId)));
    }
  };

  const handlePrintOrders = async (sortedOrders: WebPrinterTicketOrder[]) => {
    if (handleWebPrintOrder && printMethod === "WebPrint") {
      sortedOrders.forEach(async o => {
        await handleWebPrintOrder(o);
      });
    }
  };

  const sortedOrders = useMemo(
    () => sortMenuOrdersWithOverrides([...menuOrders, ...mealMenuPlateOrders], pickupTimeOverrides),
    [menuOrders, mealMenuPlateOrders, pickupTimeOverrides]
  );

  const filteredOrdersWithDiningOption = useMemo(() => {
    if (selectedDiningOption === KDSOrderDiningOption.None) {
      return sortedOrders;
    }

    return sortedOrders.filter(
      item =>
        item.__typename !== "MenuOrder" ||
        (selectedDiningOption === KDSOrderDiningOption.ToGo && item.isToGo) ||
        (selectedDiningOption === KDSOrderDiningOption.DineIn && item.isDineIn) ||
        (selectedDiningOption === KDSOrderDiningOption.ViewAll && (item.isDineIn || item.isToGo))
    );
  }, [selectedDiningOption, sortedOrders]);

  const sortedAndFilteredOrders = useMemo(
    () =>
      filteredOrdersWithDiningOption.filter(o =>
        statusWhitelist.includes(reduceStatuses(o.itemsByDiningStation.map(oo => oo.status)))
      ),
    [filteredOrdersWithDiningOption, statusWhitelist]
  );

  const unfilteredOrdersForReconcile = useMemo(
    () => [...unfilteredMenuOrders, ...unfilteredMealMenuPlateOrders],
    [unfilteredMenuOrders, unfilteredMealMenuPlateOrders]
  );

  const handleSelectAllShownOrders = () => {
    const shownOrderIds = sortedAndFilteredOrders.filter(o => o.__typename === "MenuOrder").map(order => order.id);
    const shownPlateOrderIds = sortedAndFilteredOrders.filter(o => o.__typename === "MealMenuPlateOrder").map(order => order.id);
    setSelectedOrderIds(new Set([...selectedOrderIds, ...shownOrderIds]));
    setSelectedPlateOrderIds(new Set([...selectedPlateOrderIds, ...shownPlateOrderIds]));
  };

  const handleStripReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      setPickupTimeOverrides(prev =>
        overridesAfterIndexMove(sortedAndFilteredOrders, fromIndex, toIndex, prev, unfilteredOrdersForReconcile)
      );
    },
    [sortedAndFilteredOrders, unfilteredOrdersForReconcile]
  );

  const handleReorderDialogSave = useCallback(
    (desiredOrder: readonly GenericOrderPreview[]) => {
      setPickupTimeOverrides(prev => overridesFromDesiredOrder(desiredOrder, prev, unfilteredOrdersForReconcile));
    },
    [unfilteredOrdersForReconcile]
  );

  const renderSummary = useCallback(
    () => (
      <KdsTicketSummary
        statusWhitelist={statusWhitelist}
        setStatusWhitelist={setStatusWhitelist}
        sortedOrders={[...filteredOrdersWithDiningOption]}
        setPrintAll={setPrintAll}
        setDisplayMenuItemDialog={setDisplayMenuItemDialog}
        inBulkSelect={isBulkSelect}
        activateBulkSelect={handleActivateBulkSelect}
        proceedBulkSelect={handleProceedBulkSelect}
        selectedOrderIds={[...selectedOrderIds]}
        selectedPlateOrderIds={[...selectedPlateOrderIds]}
        cancelBulkSelect={handleCancelBulkSelect}
        selectAllShownOrders={handleSelectAllShownOrders}
        deselectAll={handleDeselectAll}
        openReorderDialog={() => setIsReorderDialogOpen(true)}
        canReorder={sortedAndFilteredOrders.length > 0}
      />
    ),
    [statusWhitelist, filteredOrdersWithDiningOption, isBulkSelect, selectedOrderIds, selectedPlateOrderIds, sortedAndFilteredOrders]
  );

  const renderTicket = useCallback(
    (order: GenericOrderPreview, _index: number) => {
      if (order.__typename === "MenuOrder") {
        return (
          <KdsTicket
            key={order.id}
            orderPreview={order}
            diningStationIds={diningStationIds}
            setSelectedWebPrinterTicketOrder={setSelectedWebPrinterTicketOrder}
            menuOrderItemIdsToDiningStationName={menuOrderItemIdsToDiningStationName}
            inBulkSelect={isBulkSelect}
            selectForBulkSelect={handleSelectForBulkSelect}
            isSelected={selectedOrderIds.has(order.id)}
          />
        );
      }

      return (
        <PlateOrderKDSTicket
          key={order.id}
          orderPreview={order}
          diningStationIds={diningStationIds}
          setSelectedWebPrinterTicketOrder={setSelectedWebPrinterTicketOrder}
          menuOrderItemIdsToDiningStationName={menuOrderItemIdsToDiningStationName}
          inBulkSelect={isBulkSelect}
          selectForBulkSelect={handleSelectPlateForBulkSelect}
          isSelected={selectedPlateOrderIds.has(order.id)}
        />
      );
    },
    [
      diningStationIds,
      menuOrderItemIdsToDiningStationName,
      isBulkSelect,
      selectedOrderIds,
      selectedPlateOrderIds,
    ]
  );

  return (
    <>
      <KdsTicketStrip
        orders={sortedAndFilteredOrders}
        renderSummary={renderSummary}
        renderTicket={renderTicket}
        onReorder={handleStripReorder}
        dragDisabled={isBulkSelect}
      />
      {isReorderDialogOpen && (
        <ReorderTicketsDialog
          open={isReorderDialogOpen}
          orders={sortedAndFilteredOrders}
          onClose={() => setIsReorderDialogOpen(false)}
          onSave={handleReorderDialogSave}
        />
      )}
      {isBulkSelectDialogOpen && (
        <BulkSelectDialog
          menuOrderItemIdsToDiningStationName={menuOrderItemIdsToDiningStationName}
          sortedOrderPreviews={[...sortMenuOrders(unfilteredMenuOrders.filter(order => selectedOrderIds.has(order.id)))]}
          sortedPlateOrderPreviews={[
            ...sortMenuOrders(unfilteredMealMenuPlateOrders.filter(plateOrder => selectedPlateOrderIds.has(plateOrder.id))),
          ]}
          diningStationIds={diningStationIds}
          onClose={() => setIsBulkSelectDialogOpen(false)}
          handleCompleteOrders={handleCompleteOrders}
          handleCookOrders={handleCookOrders}
          handleMarkOrdersNew={handleMarkOrdersNew}
          handleCancelOrders={handleCancelOrders}
          handleWebPrintMany={handlePrintOrders}
          makeHandleAirPrintMany={makeHandleAirPrintFn}
          printMethod={printMethod}
        />
      )}
      {selectedWebPrinterTicketOrder && (
        <ChangeStatusDialog
          order={selectedWebPrinterTicketOrder}
          onClose={() => setSelectedWebPrinterTicketOrder(null)}
          handleWebPrintOrder={handleWebPrintOrder}
          makeHandleAirPrintFn={makeHandleAirPrintFn}
          printMethod={printMethod}
          handleCancelOrder={handleCancelOrder}
          handleCookOrder={handleCookOrder}
          handleCompleteOrder={handleCompleteOrder}
          handleMarkOrderNew={handleMarkOrderNew}
          handleCancelPlateOrder={handleCancelPlateOrder}
          handleCompletePlateOrder={handleCompletePlateOrder}
          handleCookPlateOrder={handleCookPlateOrder}
          handleMarkPlateOrderNew={handleMarkPlateOrderNew}
        />
      )}
      {printAll && (
        <PrintAllDialog
          menuOrderItemIdsToDiningStationName={menuOrderItemIdsToDiningStationName}
          handleWebPrintMany={handlePrintOrders}
          makeHandleAirPrintMany={makeHandleAirPrintFn}
          printMethod={printMethod}
          diningStationIds={diningStationIds}
          mealMenuIds={mealMenuIds}
          sortedAndFilteredOrders={sortedAndFilteredOrders}
          onClose={() => setPrintAll(false)}
          open={printAll}
        />
      )}
      {displayMenuItemDialog && (
        <AnalyzeDialog
          diningStationIds={diningStationIds}
          mealMenuIds={mealMenuIds}
          sortedAndFilteredOrders={sortedAndFilteredOrders}
          onClose={() => setDisplayMenuItemDialog(false)}
          open={displayMenuItemDialog}
        />
      )}
    </>
  );
};

export default KdsDialogContent;
